import { Alert, Collapse, Descriptions, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  DecodedTypedData,
  TypedDataApproval,
  TypedDataChainCheck,
} from '@/lib/walletconnect/typedData';

const { Text, Paragraph } = Typography;

/** Unix-seconds deadline → readable date; "No expiry" for absent/far-future values. */
function useDeadlineLabel() {
  const { t } = useTranslation();
  return (deadline: string | null): string => {
    if (deadline === null) return t('walletconnect.typed.never');
    const seconds = Number(deadline);
    // Beyond year ~9999 is a "never expires" sentinel (e.g. uint48/uint256 max).
    if (!Number.isFinite(seconds) || seconds <= 0 || seconds > 253402300799) {
      return t('walletconnect.typed.never');
    }
    return new Date(seconds * 1000).toLocaleString();
  };
}

function ApprovalRow({ approval }: { approval: TypedDataApproval }) {
  const { t } = useTranslation();
  const deadlineLabel = useDeadlineLabel();
  const amount =
    approval.kind === 'dai_permit'
      ? approval.unlimited
        ? t('walletconnect.typed.full_allowance')
        : t('walletconnect.typed.revoke')
      : approval.unlimited
        ? t('walletconnect.typed.unlimited')
        : (approval.amount ?? '—');
  return (
    <Descriptions
      column={1}
      size="small"
      bordered
      data-testid="typed-data-approval"
      title={<Tag color="red">{t(`walletconnect.typed.kinds.${approval.kind}`)}</Tag>}
    >
      <Descriptions.Item label={t('walletconnect.typed.token')}>
        <Text code copyable className="break-all" style={{ fontSize: 12 }}>
          {approval.token}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label={t('walletconnect.typed.spender')}>
        <Text code copyable className="break-all" style={{ fontSize: 12 }}>
          {approval.spender}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label={t('walletconnect.typed.amount')}>
        <Text strong type={approval.unlimited ? 'danger' : undefined} data-testid="typed-amount">
          {amount}
        </Text>
      </Descriptions.Item>
      <Descriptions.Item label={t('walletconnect.typed.expires')}>
        {deadlineLabel(approval.deadline)}
      </Descriptions.Item>
    </Descriptions>
  );
}

interface TypedDataSummaryProps {
  /** null when the JSON could not be decoded. */
  decoded: DecodedTypedData | null;
  chainCheck: TypedDataChainCheck | null;
  vaultChainId?: number;
  rawJson: string;
}

/**
 * What an EIP-712 signature authorises, decoded (ENTX-A-4): permits and
 * Permit2 allowances are spelled out (token, spender, amount, expiry), the
 * domain chain is checked against the vault, and the raw JSON is secondary.
 */
export function TypedDataSummary({
  decoded,
  chainCheck,
  vaultChainId,
  rawJson,
}: TypedDataSummaryProps) {
  const { t } = useTranslation();

  if (!decoded) {
    return (
      <Space orientation="vertical" size="small" style={{ width: '100%' }}>
        <Alert type="error" showIcon title={t('walletconnect.typed.invalid')} />
        <Paragraph code style={{ maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {rawJson}
        </Paragraph>
      </Space>
    );
  }

  return (
    <Space
      orientation="vertical"
      size="small"
      style={{ width: '100%' }}
      data-testid="typed-data-summary"
    >
      {decoded.undecodable !== null && (
        <Alert
          type="error"
          showIcon
          data-testid="typed-data-undecodable"
          title={t('walletconnect.typed.undecodable_title')}
          description={t(`walletconnect.typed.undecodable.${decoded.undecodable}`)}
        />
      )}
      {chainCheck === 'mismatch' && (
        <Alert
          type="error"
          showIcon
          data-testid="typed-data-wrong-chain"
          title={t('walletconnect.typed.wrong_chain', {
            chainId: String(decoded.domain.chainId),
            vaultChainId: String(vaultChainId ?? '—'),
          })}
        />
      )}
      {chainCheck === 'missing' && (
        <Alert type="warning" showIcon title={t('walletconnect.typed.no_chain')} />
      )}
      {decoded.approvals.length > 0 && (
        <Alert
          type="error"
          showIcon
          data-testid="typed-data-permit-warning"
          title={t('walletconnect.typed.permit_title')}
          description={t('walletconnect.typed.permit_desc')}
        />
      )}
      {decoded.approvals.map((approval, i) => (
        <ApprovalRow key={`${approval.token}-${String(i)}`} approval={approval} />
      ))}
      {decoded.isOrder && (
        <Alert
          type="warning"
          showIcon
          title={t('walletconnect.typed.order_title')}
          description={t('walletconnect.typed.order_desc')}
        />
      )}
      <Descriptions column={1} size="small" bordered title={t('walletconnect.typed.title')}>
        <Descriptions.Item label={t('walletconnect.typed.primary_type')}>
          <Text code>{decoded.primaryType}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('walletconnect.typed.domain')}>
          {[decoded.domain.name, decoded.domain.version].filter(Boolean).join(' · ') || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('walletconnect.typed.domain_chain')}>
          {decoded.domain.chainId ?? '—'}
        </Descriptions.Item>
        {decoded.domain.verifyingContract && (
          <Descriptions.Item label={t('walletconnect.typed.verifying_contract')}>
            <Text code className="break-all" style={{ fontSize: 12 }}>
              {decoded.domain.verifyingContract}
            </Text>
          </Descriptions.Item>
        )}
      </Descriptions>
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'fields',
            label: t('walletconnect.typed.fields'),
            children: (
              <div className="vstack gap-2">
                {decoded.fields.map((f) => (
                  <div key={f.path}>
                    <Text type="secondary" className="fs-12">
                      {f.path}:{' '}
                    </Text>
                    <Text className="mono fs-12 break-all">{f.value}</Text>
                  </div>
                ))}
              </div>
            ),
          },
          {
            key: 'raw',
            label: t('walletconnect.typed.raw'),
            children: (
              <Paragraph
                code
                style={{ maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}
              >
                {rawJson}
              </Paragraph>
            ),
          },
        ]}
      />
    </Space>
  );
}
